import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Field, reduxForm, reset } from 'redux-form';
import { required, email, length } from 'redux-form-validators';
import PropTypes from 'prop-types';

import { DefaultButton, ReduxTextField, CheckboxField } from 'common/components';
import { withTheme } from 'common/styling/theme';
import { isAgreementAutoAcknowledgedBroker } from 'common/utils';
import TelegramAuthWidget from '../../../_components/TelegramAuthWidget';

import { changeAuthStep, login, loginWithTelegram, resetAuth, setAuthorized } from '../../_redux';

import { staticStyles, getDynamicStyles } from './style';

const LoginForm = ({
  setRememberUser,
  loginWithTelegram,
  handleSubmit,
  pristine,
  submitting,
  invalid,
  authError,
  error,
  dispatch,
  isTelegramAuth,
  theme,
  salesCode,
  share,
  brokerId,
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;
  const telegramDataJson = sessionStorage.getItem('telegramData');
  const telegramData = JSON.parse(telegramDataJson || '{}');
  const [autofilled, setAutofilled] = useState(false);
  const [ifDisabled, setIfDisabled] = useState(true);
  const [needAgreement, setNeedAgreement] = useState(false);
  const agreementAutoAcknowledged = isAgreementAutoAcknowledgedBroker(brokerId);
  const [agreementConfirmed, setAgreementConfirmed] = useState(agreementAutoAcknowledged);
  const [showNoticeModal, setShowNoticeModal] = useState(false);

  // 指定 broker（如 10/229）自动确认协议：broker 配置到达后同步勾选状态
  useEffect(() => {
    if (agreementAutoAcknowledged) {
      setAgreementConfirmed(true);
    }
  }, [agreementAutoAcknowledged]);

  // 在提交表单时，将salesCode和agreementConfirmed包含在发送的数据中
  const submit = handleSubmit(formValues => {
    // 如果需要协议确认且用户未勾选，显示提示弹窗
    if (needAgreement && !agreementConfirmed) {
      setShowNoticeModal(true);
      return;
    }

    const submitData = { ...formValues, ...(isTelegramAuth ? telegramData : {}) };

    // 如果表单中没有salesCode但props中有，则添加salesCode
    if (salesCode && !submitData.salesCode) {
      submitData.salesCode = salesCode;
    }

    if (share && !submitData.share) {
      submitData.share = share;
    }

    // 如果需要协议确认，添加agreementConfirmed标志
    if (needAgreement && agreementConfirmed) {
      submitData.agreementConfirmed = true;
    }

    return login(submitData, dispatch);
  });

  // 处理Sign In按钮点击，当需要协议但未勾选时显示弹窗
  const handleSignInClick = e => {
    if (needAgreement && !agreementConfirmed) {
      e.preventDefault();
      setShowNoticeModal(true);
    }
  };

  const handleTelegramAuth = data => loginWithTelegram({ ...data, ...(share ? { share } : {}) });

  const handleRememberUserChange = e => {
    setRememberUser(e.target.checked);
  };

  useEffect(() => {
    setIfDisabled(true);
    if (autofilled === 'onAutoFillStart') {
      setIfDisabled(false);
    }
  }, [autofilled]);

  // 检测是否需要协议确认，同时弹出提示弹窗
  useEffect(() => {
    if (error && error.needAgreement && !agreementAutoAcknowledged) {
      setNeedAgreement(true);
      setShowNoticeModal(true);
    }
  }, [error, agreementAutoAcknowledged]);

  return (
    <form id="main-login-form" className="LoginForm" onSubmit={submit}>
      <div className="LoginForm__inputs-inner">
        <Field
          component={ReduxTextField}
          type="email"
          name="emailOrPhone"
          textId="authEmail"
          validate={[required(), email()]}
          disabled={submitting}
          onAutofill={setAutofilled}
        />
        <Field
          component={ReduxTextField}
          type="password"
          name="password"
          textId="authPassword"
          validate={[required(), length(6)]}
          disabled={submitting}
          onAutofill={setAutofilled}
        />
        {/* 在密码输入框下方添加隐藏的salesCode输入框 */}
        <Field component="input" type="hidden" name="salesCode" initialValue={salesCode || ''} />
        <Field component="input" type="hidden" name="share" initialValue={share || ''} />
        {needAgreement && !agreementAutoAcknowledged && (
          <div className="LoginForm__agreement-checkbox">
            <input
              type="checkbox"
              id="loginAgreement"
              checked={agreementConfirmed}
              onChange={() => setAgreementConfirmed(prev => !prev)}
            />
            <label htmlFor="loginAgreement" className="login-agreement-label">
              <span className="login-agreement-check-box">
                <svg viewBox="0 0 14 11" fill="none">
                  <polyline
                    points="1.5 6 4.5 9 10.5 1"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="login-agreement-check-text">
                <FormattedMessage
                  id="authAgreementConfirmation"
                  values={{
                    link: (
                      <a
                        href="/agreements/Comprehensive-Agreements.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}>
                        <FormattedMessage id="authComprehensiveAgreements" />
                      </a>
                    ),
                  }}
                />
              </span>
            </label>
          </div>
        )}
        <Field
          component={CheckboxField}
          name="rememberMe"
          disabled={submitting}
          textId="rememberMe"
          onChange={handleRememberUserChange}
        />
      </div>
      <div className="ForgotPass__inner">
        <FormattedMessage id="authForgotPassword">
          {txt => <Link to="/auth/password-reset">{txt}</Link>}
        </FormattedMessage>
        <FormattedMessage id="authNoEmailConfirmation">
          {txt => <Link to="/auth/resend-email-confirmation">{txt}</Link>}
        </FormattedMessage>
      </div>
      <DefaultButton
        id="login-page-sign-in-button"
        textId="authDoLogin"
        type="submit"
        className="LoginForm__submit-button"
        loading={submitting}
        filled
        disabled={needAgreement ? !agreementConfirmed || submitting : ifDisabled ? invalid || submitting : false}
        onClick={handleSignInClick}
      />
      <TelegramAuthWidget handleTelegramAuth={handleTelegramAuth} />
      {pristine && authError && <span className="formError">{authError}</span>}
      {error && typeof error === 'string' && <strong className="formError">{error}</strong>}

      {/* Notice弹窗 */}
      {showNoticeModal && (
        <div
          className="notice-modal-overlay"
          role="button"
          tabIndex={0}
          onClick={() => setShowNoticeModal(false)}
          onKeyDown={e => {
            if (e.key === 'Escape' || e.key === 'Enter') setShowNoticeModal(false);
          }}>
          <div className="notice-modal" onClick={e => e.stopPropagation()} role="presentation">
            <div className="notice-modal-header">
              <FormattedMessage id="authNotice">{txt => <h3>{txt}</h3>}</FormattedMessage>
            </div>
            <div className="notice-modal-body">
              <FormattedMessage id="authAgreementRequiredNotice">{txt => <p>{txt}</p>}</FormattedMessage>
            </div>
            <div className="notice-modal-footer">
              <DefaultButton textId="justOk" onClick={() => setShowNoticeModal(false)} filled />
            </div>
          </div>
        </div>
      )}

      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </form>
  );
};

// 更新propTypes
LoginForm.propTypes = {
  setRememberUser: PropTypes.func.isRequired,
  loginWithTelegram: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  authError: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  dispatch: PropTypes.func.isRequired,
  isTelegramAuth: PropTypes.bool.isRequired,
  theme: PropTypes.object,
  salesCode: PropTypes.string,
  share: PropTypes.string,
  brokerId: PropTypes.number,
};

LoginForm.defaultProps = {
  authError: '',
  error: null,
  theme: {},
  salesCode: '',
  share: '',
  brokerId: null,
};

export default compose(
  withTheme(),
  withRouter,
  connect(
    state => ({
      brokerId: state.interfaceConfig.brokerId,
    }),
    {
      loginWithTelegram: data => loginWithTelegram.request(data),
    }
  ),
  reduxForm({
    form: 'login',
    onSubmitSuccess: (result, dispatch, { history }) => {
      dispatch(reset('login'));

      if (result === 'success') {
        dispatch(resetAuth(''));
        dispatch(setAuthorized());
        history.push('/');
      } else {
        dispatch(changeAuthStep(result));
      }
    },
  })
)(LoginForm);
export { LoginForm };
